<template>
  <el-dialog
    v-model="isDialogUplineTree"
    width="500"
    destroy-on-close
    center
    @close="handleDialogClose"
  >
    <div v-if="formattedTreeData">
      <p class="fw-800 fs-9 text-success">
        Total Levels: {{ items?.data?.total_levels ?? 0 }}
      </p>

      <el-tree
        style="max-width: 600px"
        :data="formattedTreeData"
        default-expand-all
        node-key="id"
      />
    </div>

    <template #footer>
      <div class="dialog-footer">
        <el-button
          variant="flat"
          size="small"
          rounded="md"
          type="danger"
          @click="handleDialogClose"
          >Close</el-button
        >
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import { defineProps, defineEmits, ref, inject, computed, watch } from "vue";
import ApiService from "@/core/services/Downlines.ts";
import { LoadingService } from "@/core/plugins/loading";
import { ElNotification } from "element-plus";

import type Node from "element-plus/es/components/tree/src/model/node";
import type { DragEvents } from "element-plus/es/components/tree/src/model/useDragNode";
import type {
  AllowDropType,
  NodeDropType,
} from "element-plus/es/components/tree/src/tree.type";

const $loading = inject("$loading") as LoadingService;

const items = ref<{
  data: { total_levels: number; tree: RawTreeNode[] };
} | null>(null);

const props = defineProps({
  modelValue: Boolean,
  selectUser: Object,
});

const emit = defineEmits();

const isDialogUplineTree = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
  },
});

const handleDialogClose = () => {
  items.value = null;
  emit("update:modelValue", false);
};

const getDetail = async (user: any) => {
  $loading.startLoading();
  try {
    const resp = await ApiService.uplineTree(user);
    if (resp.status === "success") {
      items.value = resp;
    } else {
      emit("update:modelValue", false);
    }

    ElNotification({
      title: resp.status,
      message: resp.msg,
      type: resp.status,
    });
  } catch (error) {
    // console.error("Error fetching tree data:", error);
    ElNotification({
      title: "Error",
      message: "Failed to load tree data.",
      type: "error",
    });
  }
  $loading.endLoading();
};

watch(
  () => props.selectUser,
  (newVal) => {
    if (newVal) {
      getDetail(newVal);
    }
  },
  { immediate: true }
);

const formattedTreeData = computed(() => {
  if (!items.value?.data?.tree) return [];
  const sortedNodes = [...items.value.data.tree].sort(
    (a, b) => a.level - b.level
  );
  return sortedNodes.map((node) => formatNode(node));
});

const formatNode = (node: any) => {
  const label = `${node.level}: ${node.username}`;
  return {
    ...node,
    label: label + (node.nickname ? ` (${node.nickname})` : ""),
    children: node.children
      ? node.children.map((child) => formatNode(child))
      : [],
  };
};
</script>

<style>
.el-tree-node {
  font-weight: 700;
  font-size: 15px;
}
</style>
